import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, TextField, Typography, InputAdornment, Autocomplete } from '@mui/material';
import { petsPathTo } from '../utils/Api';
import { PetsContext } from '../contexts/PetsContext';
import SnackbarContext from '../contexts/SnackbarContext';
import { TagsContext } from '../contexts/TagsContext';
import { useNavigate } from 'react-router-dom';
import AvatarUploader from './AvatarUploader';
import { DatePicker } from '@mui/x-date-pickers';
import moment from 'moment';

/* Icons */
import CloseIcon from '@mui/icons-material/Close';
import { layoutButtonPillRadius, layoutIconButtonRadius, layoutShopSheetRadius } from '../theme/colors';

/* Padrão Campos Pet */
const FormPet = ({ label, require, children, sx = {} }) => (
  /* Info Pet */
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch', ...sx }}>
    {/* Título Campo */}
    <Typography sx={{ color: '#000', fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, lineHeight: '14px' }}>
      {label}{require && '*'}
    </Typography>

    {/* TextField // Autocomplete*/}
    {children}
  </Box>
);

const PetDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { petsList, setPetsList } = useContext(PetsContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const [pet, setPet] = useState(null);
  const { tags } = useContext(TagsContext);
  const speciesTags = tags.filter(tag => tag.category === 'species');
  const sizeTags = tags.filter(tag => tag.category === 'size');
  const ageGroupTags = tags.filter(tag => tag.category === 'age_group');
  const sexTags = tags.filter(tag => tag.category === 'sex');

  useEffect(() => {
    if (pet) {
      document.title = `${pet.name} - TudoPets.app`;
    }
  }, [pet]);

  useEffect(() => {
    setPet(petsList.find((pet) => pet.id === parseInt(id)));
  }, [petsList, id]);

  const handleFormFieldChange = (e) => {
    const { name, value } = e.target;
    setPet((prevPet) => ({
      ...prevPet,
      [name]: value,
    }));
  };

  const handleAvatarChange = (value) => {
    petsPathTo.update_avatar(id, {
      avatar: value
    }).then(result => {
      setPetsList(result.data);
      openSnackbar("Avatar atualizado com sucesso!", "success");
    })
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    petsPathTo.update(id, pet).then(result => {
      // setPetsList(result.data);
      navigate('/mypets');
      openSnackbar("Pet atualizado com sucesso!", "success");
    })
  }

  if (!pet) {
    return <div>Carregando...</div>;
  }

  const containerFlex = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  }

  const buttonStyle = {
    display: 'flex',
    width: '100%',
    p: '16px 0',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    borderRadius: layoutButtonPillRadius,
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px'
  }

  return (
    <Box sx={{ ...containerFlex, padding: '24px', gap: 3, flexShrink: 0, bgcolor: 'surface.card', borderRadius: layoutShopSheetRadius }}>
      {/* Título e Close */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, alignSelf: 'stretch', justifyContent: 'space-between' }}>
        <Typography variant="pageTitle" textAlign="left" sx={{ width: '100%', m: 0, color: '#263238' }}>
          Editar pet {/* Usar mesmo esquema que o formulário do endereço? */}
        </Typography>

        {/* Botão para Voltar */}
        <Button data-no-shadow="true" onClick={() => window.history.back()} sx={{ borderRadius: layoutIconButtonRadius }}>
          <CloseIcon sx={{ color: '#000' }} />
        </Button>
      </Box>

      {/* Foto Pet */}
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', alignSelf: 'stretch' }}>
        <AvatarUploader handleAvatarChange={handleAvatarChange} pet={pet} />
      </Box>

      {/* Formulário */}
      <Box sx={{ ...containerFlex, gap: 2, alignSelf: 'stretch' }}>
        {/* Nome Pet */}
        <FormPet label="Nome" require={true}>
          <TextField
            //label="Nome"
            variant="outlined"
            value={pet.name || ''}
            name="name"
            placeholder="Nome do pet"
            fullWidth
            onChange={handleFormFieldChange}
            autoComplete="off"
            sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
          />
        </FormPet>

        {/* Espécie Pet */}
        <FormPet label="Espécie" require={true}>
          <Autocomplete
            options={speciesTags}
            getOptionLabel={(option) => option.name}
            value={speciesTags.find((tag) => tag.id === pet.species_tag_id) || null}
            name="species_tag_id"
            sx={{ width: '100%' }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione"
                variant="outlined"
                fullWidth
                sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                species_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
        </FormPet>

        {/* Raça Pet */}
        <FormPet label="Raça" require={false}>
          <TextField
            //label="Raça"
            variant="outlined"
            value={pet.breed || ''}
            name="breed"
            fullWidth
            onChange={handleFormFieldChange}
            autoComplete="off"
            sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
          />
        </FormPet>

        {/* Sexo Pet */}
        <FormPet label="Sexo" require={false}>
          <Autocomplete
            options={sexTags}
            getOptionLabel={(option) => option.name}
            value={sexTags.find((tag) => tag.id === pet.sex_tag_id) || null}
            name="sex_tag_id"
            sx={{ width: '100%' }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione"
                variant="outlined"
                fullWidth
                sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                sex_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
        </FormPet>

        {/* Niver Pet */}
        <FormPet label="Data de nascimento" require={false}>
          <DatePicker
            placeholder="DD/MM/YYYY"
            variant="outlined"
            value={moment(pet.birth_date).isValid() ? moment(pet.birth_date) : null}
            name="birth_date"
            onChange={(e) => setPet(prevPet => ({ ...prevPet, birth_date: e }))}
            sx={{ width: '100%', bgcolor: '#F5F8FA', borderRadius: '24px' }}
            format="DD/MM/YYYY"
          />
        </FormPet>

        {/* Idade Pet */}
        <FormPet label="Faixa de idade" require={false}>
          <Autocomplete
            options={ageGroupTags}
            getOptionLabel={(option) => option.name}
            value={ageGroupTags.find((tag) => tag.id === pet.age_group_tag_id) || null}
            name="age_group_tag_id"
            sx={{ width: '100%' }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Selecione"
                variant="outlined"
                fullWidth
                sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
              />
            )}
            onChange={(event, newValue) => {
              setPet((prevPet) => ({
                ...prevPet,
                age_group_tag_id: newValue ? newValue.id : null,
              }));
            }}
          />
        </FormPet>

        {/* Porte e Peso*/}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', alignSelf: 'stretch' }}>

          {/* Porte Pet */}
          <FormPet label="Porte" require={false} sx={{ flex: 1 }}>
            <Autocomplete
              options={sizeTags}
              getOptionLabel={(option) => option.name}
              value={sizeTags.find((tag) => tag.id === pet.size_tag_id) || null}
              name="size_tag_id"
              sx={{ width: '100%' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Selecione"
                  variant="outlined"
                  fullWidth
                  sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
                />
              )}
              onChange={(event, newValue) => {
                setPet((prevPet) => ({
                  ...prevPet,
                  size_tag_id: newValue ? newValue.id : null,
                }));
              }}
            />
          </FormPet>

          {/* Peso Pet */}
          <FormPet label="Peso" require={false} sx={{ flex: 1 }}>
            <TextField
              //label="Peso"
              variant="outlined"
              value={pet.weight || ''}
              name="weight"
              type="number"
              fullWidth
              sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
              onChange={handleFormFieldChange}
              InputProps={{
                endAdornment: <InputAdornment position="end">Kg</InputAdornment>,
              }}
            />
          </FormPet>
        </Box>

        {/* Cor Pet */}
        <FormPet label="Cor" require={false}>
          <TextField
            //label="Cor"
            variant="outlined"
            value={pet.color || ''}
            name="color"
            fullWidth
            onChange={handleFormFieldChange}
            autoComplete="off"
            sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
          />
        </FormPet>

        {/* Descrição Pet */}
        <FormPet label="Descrição" require={false}>
          <TextField
            placeholder="Descreva seu amigo de quatro patas"
            variant="outlined"
            value={pet.description || ''}
            name="description"
            fullWidth
            onChange={handleFormFieldChange}
            autoComplete="off"
            multiline  // Para permitir múltiplas linhas
            rows={3}   // Número aproximado de linhas para 98px
            sx={{ bgcolor: '#F5F8FA', borderRadius: '24px' }}
          />
        </FormPet>

        {/* Botões Salvar/Cancelar */}
        <Box sx={{ ...containerFlex, gap: '10px', alignSelf: 'stretch' }}>

          {/* Botão Salvar */}
          <Button onClick={handleSubmit} variant="contained"
            sx={{ ...buttonStyle }}
          >
            Salvar
          </Button>

          {/* Botão Cancelar */}
          <Button onClick={() => navigate('/mypets')} color="error" variant="contained"
            sx={{ ...buttonStyle, bgcolor: '#35454D' }}
          >
            Cancelar
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default PetDetails;
